import React, { useState, useEffect, useContext, useRef } from 'react';
import { 
  Box, Typography, Card, CardContent, Chip, Divider, Button, Avatar, IconButton, Grid
} from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import AuthContext from "flexbiz-core/components/auth/Context";
import { formatCurrency } from "flexbiz-core/utils";
import Counter from "flexbiz-core/components/Counter";

// Icons
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import PaymentsIcon from '@mui/icons-material/Payments';
import CancelIcon from '@mui/icons-material/Cancel';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import EmailIcon from '@mui/icons-material/Email';

// Component Quản lý Thần thánh
import DataListManager from 'flexbiz-core/components/DataListManager';


const IOS_BLUE = '#007aff';

export default function BrokerSalesHistoryPage() {
  const history = useHistory();
  const location = useLocation();
  const context = useContext(AuthContext);

  const queryParams = new URLSearchParams(location.search);
  const ma_kh_url = queryParams.get('ma_kh');

  const managerRef = useRef(null);

  // ================= 1. STATE & EFFECT FETCH THÔNG TIN KHÁCH HÀNG =================
  const [customerInfo, setCustomerInfo] = useState(null);

  useEffect(() => {
    if (ma_kh_url) {
      const fetchCustomerDetails = async () => {
        try {
          const res = await context.apis.asyncGetList(context.userInfo.token, "customer", {
            condition: { ma_kh: ma_kh_url },
            limit: 1
          });
          if (res.length > 0) {
            setCustomerInfo(res[0]);
          }
        } catch (error) {
          console.error("Lỗi khi tải thông tin khách hàng:", error);
        }
      };
      fetchCustomerDetails();
    }
  }, [ma_kh_url, context]);

  // ================= 2. ĐỊNH NGHĨA FIELDS =================
  const historyFields = [
    { field: "ma_nd", label: "Mã BĐS", type: "String" },
    { field: "ten_nguoi_mua", label: "Tên khách hàng", type: "String" },
    { field: "dien_thoai", label: "Số điện thoại", type: "String" },
    { field: "gia_ban_thuc", label: "Giá bán thực", type: "Number" },
    { 
      field: "trang_thai", label: "Trạng thái", type: "Select", 
      api: "trangthai", apiCondition: { ma_ct:"NHADAT_MOIGIOI_BAN" }, apiMapping: { value: "ma_trang_thai", label: "ten_trang_thai" } 
    },
    { field: "date_created", label: "Thời gian tạo", type: "Date",searchable:false }
  ];

  // ================= 3. UTILS =================
  const formatDate = (isoString) => {
    if (!isoString) return '---';
    return new Date(isoString).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch (String(status)) {
      case '1': return '#ff9500'; 
      case '2': return IOS_BLUE; 
      case '3': return '#34c759'; 
      case '9': return '#ff3b30'; 
      case '0': 
      default: return '#8e8e93'; 
    }
  };

  const getStatusLabel = (status, defaultLabel) => {
    if(defaultLabel) return defaultLabel;
    switch (String(status)) {
      case '0': return 'Mới tạo';
      case '1': return 'Đã cọc';
      case '2': return 'Ký HĐMB';
      case '3': return 'Đã giao sổ';
      case '9': return 'Đã hủy';
      default: return defaultLabel || 'Mới tạo';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts[parts.length - 1].charAt(0).toUpperCase();
  };

  // ================= 4. HÀNH ĐỘNG HỦY GIAO DỊCH =================
  const handleCancelTransaction = (item) => {
    context.alert("Bạn có chắc chắn muốn huỷ giao dịch này không? Hành động này không thể hoàn tác.", async (ok) => {
      if (ok) {
        context.setProgressStatus(true);
        try {
          const payload = { _id: item._id, trang_thai: "9" };
          const res = await context.apis.asyncPostList(context.userInfo.token, "nhadat_moigioi_ban", payload);
          
          if (res?.error || res?.data?.error) throw new Error(res?.error || res?.data?.error);

          if (managerRef.current) {
            managerRef.current.updateLocalItem({ 
              _id: item._id, 
              trang_thai: "9",
              ten_trang_thai: "Đã hủy" 
            });
          }

          context.alert("Đã huỷ giao dịch thành công!");
        } catch (err) {
          context.alert(err.message || "Lỗi khi huỷ giao dịch", null, "red");
        } finally {
          context.setProgressStatus(false);
        }
      }
    }, "red");
  };

  // ================= 5. RENDER BANNERS (HỒ SƠ KHÁCH HÀNG + THỐNG KÊ) =================
  const renderBanners = () => {
    const conditionCount = ma_kh_url 
      ? { user_created: context.userInfo?.email, ma_kh: ma_kh_url } 
      : { user_created: context.userInfo?.email };

    return (
      <Box sx={{ mb: 1 }}>
        {/* PROFILE KHÁCH HÀNG */}
        {customerInfo && (
          <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.05)', border: 'none' }}>
            <CardContent sx={{ p: '16px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <Avatar sx={{ bgcolor: IOS_BLUE, width: 56, height: 56, mr: 1.5, fontWeight: 'bold', fontSize: '1.5rem' }}>
                  {getInitials(customerInfo.ten_kh)}
                </Avatar>
                
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                  <Typography variant="h6" fontWeight="bold" color="#1c1c1e" noWrap>
                    {customerInfo.ten_kh}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Mã KH: <Typography component="span" fontWeight="bold" color="primary">{customerInfo.ma_kh}</Typography>
                  </Typography>
                </Box>
                
                {/* NÚT LIÊN HỆ NHANH */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {customerInfo.dien_thoai && (
                    <IconButton component="a" href={`tel:${customerInfo.dien_thoai}`} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}>
                      <PhoneInTalkIcon fontSize="small" />
                    </IconButton>
                  )}
                  {customerInfo.email && (
                    <IconButton component="a" href={`mailto:${customerInfo.email}`} size="small" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }}>
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 1.5, borderStyle: 'dashed' }} />
              
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2"><Typography component="span" color="text.secondary">SĐT: </Typography> {customerInfo.dien_thoai || '---'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2"><Typography component="span" color="text.secondary">CCCD: </Typography> {customerInfo.cccd || '---'}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2"><Typography component="span" color="text.secondary">Địa chỉ: </Typography> {customerInfo.dia_chi || '---'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* BANNER ĐẾM SỐ LƯỢNG */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#e3f2fd', p: 1.5, borderRadius: 3, border: '1px dashed #90caf9' }}>
          <Typography variant="subtitle2" color="#1565c0" fontWeight="bold">
            Tổng số giao dịch: <Counter code="nhadat_moigioi_ban" condition={conditionCount} />
          </Typography>
        </Box>
      </Box>
    );
  };

  // ================= 6. RENDER GIAO DIỆN TỪNG ITEM =================
  const renderHistoryCard = (item) => {
    const isNew = String(item.trang_thai) === "0";
    const statusLabel = getStatusLabel(item.trang_thai, item.ten_trang_thai);
    const statusColor = item.color || getStatusColor(item.trang_thai);

    return (
      <Card key={item._id} sx={{ 
        //mb: 2, 
        borderRadius: '16px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', 
        border: 'none' 
      }}>
        <CardContent sx={{ p: '16px !important' }}>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: IOS_BLUE }}>
              <MapsHomeWorkIcon sx={{ fontSize: 20, mr: 0.5 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                {item.ma_nd || '---'}
              </Typography>
            </Box>
            <Chip 
              label={statusLabel} 
              size="small" 
              sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 22, bgcolor: statusColor, color: 'white' }} 
            />
          </Box>

          <Box sx={{ bgcolor: '#f2f2f7', p: 1.5, borderRadius: 2, mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Tờ: <Typography component="span" fontWeight="bold" color="text.primary">{item.so_to || '---'}</Typography> | 
              Thửa: <Typography component="span" fontWeight="bold" color="text.primary">{item.so_thua || '---'}</Typography> | 
              DT: <Typography component="span" fontWeight="bold" color="text.primary">{item.dien_tich || '---'} m²</Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <AspectRatioIcon sx={{ fontSize: 16, mr: 0.5 }} />
              <Typography variant="body2">
                Kích thước: <Typography component="span" fontWeight="bold" color="text.primary">{item.chieu_ngang || '?'}m x {item.chieu_dai || '?'}m</Typography>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 0.5 }}>
              <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
              <Typography variant="body2" noWrap fontWeight="500" color="text.primary">
                {item.ten_nguoi_mua || '---'} {item.dien_thoai ? `(${item.dien_thoai})` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 0.5 }}>
              <PaymentsIcon sx={{ fontSize: 16, mr: 0.5 }} />
              <Typography variant="body2" noWrap>
                Thanh toán: <Typography component="span" fontWeight="500" color="text.primary">{item.ten_pt_thanh_toan || item.pt_thanh_toan || '---'}</Typography>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5 }} />
              <Typography variant="caption">{formatDate(item.date_created)}</Typography>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 1.5, borderStyle: 'dashed' }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Giá bán thực:</Typography>
              <Typography variant="body2" fontWeight="bold" color="#d32f2f">
                {formatCurrency(item.gia_ban_thuc || item.gia_ban)}
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography variant="caption" color="text.secondary" display="block">Hoa hồng:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#34c759' }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  {formatCurrency(item.hoa_hong > 0 ? item.hoa_hong : item.hoa_hong2)}
                </Typography>
              </Box>

              {isNew && (
                <Button 
                  size="small" 
                  variant="outlined" 
                  color="error" 
                  startIcon={<CancelIcon />}
                  onClick={() => handleCancelTransaction(item)}
                  sx={{ mt: 1, textTransform: 'none', borderRadius: 2, py: 0.2 }}
                >
                  Huỷ giao dịch
                </Button>
              )}
            </Box>
          </Box>

        </CardContent>
      </Card>
    );
  };

  return (
    <DataListManager
      ref={managerRef}
      title="Lịch sử giao dịch"
      code="nhadat_moigioi_ban"
      condition={{ 
        user_created: context.userInfo?.email, 
        ...(ma_kh_url && { ma_kh: ma_kh_url }) 
      }}
      fields={historyFields}
      
      // Ghép toàn bộ cụm Profile KH và Thống kê vào Banners
      banners={renderBanners()}
      
      quickFilterConfig={{
        field: "trang_thai",
        api: "trangthai", apiCondition: { ma_ct:"NHADAT_MOIGIOI_BAN" }, apiMapping: { value: "ma_trang_thai", label: "ten_trang_thai" } 
      }}

      canAdd={false}
      canEdit={false}
      canDelete={false}
      
      onBack={() => history.goBack()}
      renderItem={renderHistoryCard}
      actionBarPosition="top" 
    />
    
    
  );
}